<template>
    <div>
        <button class="inline-flex w-full justify-center items-center gap-2 rounded-md px-5 py-2 bg-gray-800 text-sm font-semibold text-white shadow-sm ring-2 ring-inset ring-cyan-600 hover:bg-gray-900 desktop:hidden" @click="$emit('toggle')" :aria-label="$t('filters.expand')">
            <span class="material-symbols-outlined">filter_alt</span>
            <span class="ml-2">{{ $t('filters.show') }}</span>
        </button>

        <!-- Mobile only: dims the page behind the filters modal -->
        <div v-if="expanded" class="fixed inset-0 z-40 bg-black/70 desktop:hidden" @click="$emit('toggle')"></div>

        <div ref="filtersWrapper"
            class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92vw] max-h-[90vh] overflow-y-auto overscroll-contain
                   flex flex-col gap-[1.2rem] p-4 box-border rounded-lg bg-gray-800 shadow-[0_20px_25px_-5px_rgb(0_0_0/0.4)]
                   transition-[opacity,visibility] duration-200 ease-in-out
                   desktop:visible desktop:opacity-100 desktop:pointer-events-auto desktop:static desktop:translate-x-0 desktop:translate-y-0
                   desktop:w-auto desktop:max-h-none desktop:overflow-visible desktop:shadow-none desktop:bg-transparent
                   desktop:flex-row desktop:flex-wrap desktop:items-end desktop:gap-8 desktop:px-0 desktop:pt-6 desktop:pb-8"
            :class="expanded ? 'visible opacity-100 pointer-events-auto' : 'invisible opacity-0 pointer-events-none'" role="dialog" :aria-label="$t('filters.show')">
            <!-- Mobile only: modal header -->
            <div class="flex items-center justify-between desktop:hidden">
                <span class="text-lg font-semibold text-gray-200">{{ $t('filters.show') }}</span>
                <button type="button" class="text-white" @click="$emit('toggle')">
                    <span class="material-symbols-outlined">close</span>
                </button>
            </div>

            <!-- Period filter: one range picker with quick presets; clearing it means all time -->
            <form method="GET" class="block w-full desktop:w-auto" @submit.prevent>
                <div class="flex flex-col gap-1 place-items-start">
                    <label for="dateRangeInput" class="text-gray-200">{{ $t('filters.period') }}</label>
                    <div class="w-full desktop:w-72">
                        <AppDatePicker
                            :model-value="dateRange"
                            @update:model-value="onDateRangeChange"
                            id="dateRangeInput"
                            range
                            clearable
                            quick-presets
                            :placeholder="$t('filters.presetAll')"
                        />
                    </div>
                </div>
            </form>

            <!-- Category selection dropdown -->
            <form method="GET" class="block w-full desktop:w-auto" @submit.prevent>
                <div class="flex flex-col gap-1 place-items-start">
                    <label for="subcat" class="text-gray-200">{{ $t('filters.category') }}</label>
                    <select
                        id="subcat"
                        name="subcat"
                        @change="onFilterChange('update:subcategory', $event.target.value)"
                        class="w-full desktop:w-48"
                    >
                        <option value="all">{{ $t('filters.allCategories') }}</option>
                        <optgroup v-for="(subcategories, category) in groupedSubcategories" :label="category" :key="category">
                            <option v-for="subcategory in subcategories" :value="subcategory.id" :key="subcategory.id">
                                {{ subcategory.sub }}
                            </option>
                        </optgroup>
                    </select>
                </div>
            </form>

            <!-- Recurring expenses selection dropdown -->
            <form method="GET" class="block w-full desktop:w-auto" @submit.prevent>
                <div class="flex flex-col gap-1 place-items-start">
                    <label for="recurringExpensesSelect" class="text-gray-200">{{ $t('filters.type') }}</label>
                    <select
                        id="recurringExpensesSelect"
                        name="recurringExpensesSelect"
                        :value="recurringFilter"
                        @change="onFilterChange('update:recurringFilter', $event.target.value)"
                        class="w-full desktop:w-48"
                    >
                        <option value="all">{{ $t('filters.allExpenses') }}</option>
                        <option value="current">{{ $t('filters.current') }}</option>
                        <option value="noncurrent">{{ $t('filters.nonCurrent') }}</option>
                    </select>
                </div>
            </form>

            <!-- Search -->
            <form method="GET" class="block w-full desktop:w-auto" @submit.prevent>
                <div class="flex flex-col gap-1 place-items-start relative">
                    <label for="searchInput" class="text-gray-200">{{ $t('filters.search') }}</label>
                    <input
                        class="w-full desktop:w-50"
                        name="search"
                        id="searchInput"
                        type="text"
                        :value="searchTerm"
                        @input="onSearchInput($event.target.value)"
                        @keypress.enter.prevent />
                    <!-- Clear button -->
                    <button
                        v-if="searchTerm"
                        type="button"
                        @click="$emit('clear-search')"
                        class="absolute right-0 bottom-1 w-8 h-8
                                flex items-center justify-center
                                text-red-500 hover:text-white
                                cursor-pointer"
                    >
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>
            </form>

            <!-- Show Note column -->
            <div class="w-full flex flex-row items-center gap-2 py-0 px-0 ml-0 desktop:w-auto lg:py-2">
              <label for="showNoteColumn" class="inline-flex items-center cursor-pointer">
                <input
                  class="sr-only peer"
                  name="showNoteColumn"
                  id="showNoteColumn"
                  type="checkbox"
                  :checked="showNoteColumn"
                  @change="onFilterChange('update:showNoteColumn', $event.target.checked)" />
                <div class="relative w-9 h-5 bg-gray-500 peer-checked:bg-cyan-500 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-500/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-on-accent after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                <span class="select-none ms-3 text-gray-200">{{ $t('filters.notes') }}</span>
              </label>
            </div>

            <!-- Export: applies to whatever was last applied above. On mobile the menu renders in normal flow, right below the button; on desktop it is an
                 absolutely-positioned overlay so opening it doesn't grow this row's height and shift every other bottom-aligned filter around. -->
            <div class="relative w-full mb-4 ml-0 desktop:w-auto desktop:mb-0 desktop:ml-auto" :class="{ 'pb-4 desktop:pb-0': isExportMenuOpen }" ref="exportGroup">
                <button
                    type="button"
                    class="btn-ghost w-full justify-center items-center whitespace-nowrap desktop:w-40"
                    :disabled="exporting"
                    @click="toggleExportMenu"
                >
                    <span class="material-symbols-outlined">download</span>
                    <span>{{ exporting ? $t('export.exporting') : $t('export.title') }}</span>
                    <span class="material-symbols-outlined">arrow_drop_down</span>
                </button>

                <div v-if="isExportMenuOpen" ref="exportMenu"
                     class="mt-2 p-[1px] w-full rounded-md bg-gray-800 shadow-lg ring-1 ring-inset ring-cyan-600 overflow-hidden
                            desktop:absolute desktop:top-full desktop:right-0 desktop:w-40">
                    <button type="button" @click="onExport('xlsx')" class="block w-full text-left whitespace-nowrap px-4 py-2 text-sm text-white hover:bg-gray-900 rounded-lg hover:border-0">{{ $t('export.excel') }}</button>
                    <button type="button" @click="onExport('pdf')" class="block w-full text-left whitespace-nowrap px-4 py-2 text-sm text-white hover:bg-gray-900 rounded-lg hover:border-0">{{ $t('export.pdf') }}</button>
                </div>
            </div>

            <!-- Mobile only: on desktop every change already applies live (see onFilterChange/onSearchInput), so this button would be redundant there -->
            <div class="sticky -bottom-4 -left-4 -m-4 w-[calc(100%+32px)] desktop:hidden flex flex-col gap-2 bg-gray-100 p-3 shadow-[0_-8px_6px_-1px_rgb(0_0_0/0.1)]">
              <button
                  type="button"
                  class="primary w-full whitespace-nowrap desktop:w-auto"
                  @click="$emit('apply')"
              >
                  <span class="material-symbols-outlined">check</span>
                  <span>{{ $t('filters.applyFilters') }}</span>
              </button>
            </div>

        </div>
    </div>
</template>

<script>
import AppDatePicker from '@/components/common/AppDatePicker.vue';

// Keep this in sync with the `desktop` screen in tailwind.config.js.
const DESKTOP_QUERY = '(min-width: 900px)';

// Filter bar of the Expenses page. Holds no state of its own (besides tracking desktop vs.
// mobile): every change is emitted to the parent, which owns the actual filter values.
export default {
    components: { AppDatePicker },
    props: {
        expanded: {
            type: Boolean,
            default: false,
        },
        startDate: {
            type: String,
            default: null,
        },
        endDate: {
            type: String,
            default: null,
        },
        groupedSubcategories: {
            type: Object,
            default: () => ({}),
        },
        recurringFilter: {
            type: String,
            default: 'all',
        },
        searchTerm: {
            type: String,
            default: '',
        },
        showNoteColumn: {
            type: Boolean,
            default: true,
        },
        exporting: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'toggle',
        'update:dateRange',
        'update:subcategory',
        'update:recurringFilter',
        'update:searchTerm',
        'update:showNoteColumn',
        'clear-search',
        'apply',
        'export',
    ],
    data() {
        return {
            // On desktop the filter panel is always visible alongside the table, so there's no
            // reason to make the user click Apply: each change applies right away, same as
            // before this component had an Apply button at all. On mobile the panel covers the
            // table, so changes stay a draft until Apply (see onFilterChange/onSearchInput).
            isDesktop: typeof window !== 'undefined' ? window.matchMedia(DESKTOP_QUERY).matches : false,
            searchDebounceTimer: null,
            isExportMenuOpen: false,
        };
    },
    watch: {
        // On mobile the filters are a modal: stop the page underneath from scrolling while open
        expanded(isOpen) {
            document.body.style.overflow = isOpen && !this.isDesktop ? 'hidden' : '';
        },
        isDesktop(isDesktop) {
            if (isDesktop) {
                document.body.style.overflow = '';
            }
        },
    },
    mounted() {
        this.desktopQueryList = window.matchMedia(DESKTOP_QUERY);
        this.handleDesktopQueryChange = (event) => {
            this.isDesktop = event.matches;
        };
        this.desktopQueryList.addEventListener('change', this.handleDesktopQueryChange);

        this.handleOutsideClick = (event) => {
            if (this.isExportMenuOpen && !this.$refs.exportGroup.contains(event.target)) {
                this.isExportMenuOpen = false;
            }
        };
        document.addEventListener('click', this.handleOutsideClick);
    },
    beforeUnmount() {
        document.body.style.overflow = '';
        this.desktopQueryList.removeEventListener('change', this.handleDesktopQueryChange);
        document.removeEventListener('click', this.handleOutsideClick);
        clearTimeout(this.searchDebounceTimer);
    },
    computed: {
        dateRange() {
            return this.startDate && this.endDate ? [this.startDate, this.endDate] : null;
        },
    },
    methods: {
        onDateRangeChange(range) {
            this.onFilterChange('update:dateRange', range);
        },
        // On mobile the menu opens below the fold of the scrolling modal, so bring it into view
        async toggleExportMenu() {
            this.isExportMenuOpen = !this.isExportMenuOpen;
            if (this.isExportMenuOpen) {
                await this.$nextTick();
                // scrollIntoView({ block: 'nearest' }) isn't enough: the sticky Apply bar sits over
                // the menu, which still counts as "visible", so scroll the modal to its bottom
                const wrapper = this.$refs.filtersWrapper;
                wrapper?.scrollTo({ top: wrapper.scrollHeight, behavior: 'smooth' });
            }
        },
        onExport(format) {
            this.isExportMenuOpen = false;
            this.$emit('export', format);
        },
        // Dropdowns: applies right away on desktop, stays a draft on mobile until Apply is clicked
        onFilterChange(eventName, value) {
            this.$emit(eventName, value);
            if (this.isDesktop) {
                this.$emit('apply');
            }
        },
        // Search: same idea, but debounced on desktop so it doesn't fetch on every keystroke
        onSearchInput(value) {
            this.$emit('update:searchTerm', value);
            if (this.isDesktop) {
                clearTimeout(this.searchDebounceTimer);
                this.searchDebounceTimer = setTimeout(() => this.$emit('apply'), 400);
            }
        },
    },
};
</script>
